// src/services/vimeoService.ts
import axios from "axios"

export interface VimeoVideo {
  uri: string
  name: string
  description: string
  link: string
  duration: number
  created_time: string
  pictures: {
    sizes: {
      width: number
      height: number
      link: string
    }[]
  }
}

export interface VimeoResponse {
  data: VimeoVideo[]
  paging: {
    next: string | null
    previous: string | null
  }
}

class VimeoService {
  private accessToken: string
  private userId: string

  constructor(accessToken: string, userId: string) {
    this.accessToken = accessToken
    this.userId = userId
  }

  async getVideos(page: number = 1, perPage: number = 12): Promise<VimeoResponse> {
    try {
      const response = await axios.get<VimeoResponse>(`https://api.vimeo.com/users/${this.userId}/videos`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        params: {
          page,
          per_page: perPage,
          sort: "date",
          direction: "desc",
        },
      })
      return response.data
    } catch (error) {
      console.error("Error fetching videos from Vimeo:", error)
      throw error
    }
  }

  async getVideo(videoId: string): Promise<VimeoVideo> {
    try {
      const response = await axios.get<VimeoVideo>(`https://api.vimeo.com/videos/${videoId}`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      })
      return response.data
    } catch (error) {
      console.error(`Error fetching video with ID ${videoId} from Vimeo:`, error)
      throw error
    }
  }

  // Additional method to directly fetch a specific video if you have its ID or URL
  async getVideoByUrl(vimeoUrl: string): Promise<VimeoVideo | null> {
    try {
      // Extract video ID from URL (e.g., https://vimeo.com/123456789)
      const videoId = vimeoUrl.split("/").pop()
      if (!videoId) return null

      return await this.getVideo(videoId)
    } catch (error) {
      console.error(`Error fetching video from URL ${vimeoUrl}:`, error)
      return null
    }
  }

  // Method to test if the API connection is working
  async testConnection(): Promise<boolean> {
    try {
      // Just check if we can reach the Vimeo API and authenticate
      await axios.get(`https://api.vimeo.com/${this.userId}/videos`, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      })
      return true
    } catch (error) {
      console.error("Vimeo API connection test failed:", error)
      return false
    }
  }
}

const vimeoService = new VimeoService(
  process.env.REACT_APP_VIMEO_ACCESS_TOKEN || "",
  process.env.REACT_APP_VIMEO_USER_ID || "",
)

export default vimeoService
